import { useCallback, useMemo, useState } from 'react';

import { useEntityDisplayName } from '@/lib/crm/hooks/useEntityDisplayName';
import { getDisplayName } from '@/lib/crm/utils/getDisplayName';
import type { CrmObject } from '@/types/crm';

import type { HighlightDefinition } from '../registry/entityTypeRegistry';
import { HighlightCardShell } from './HighlightCardShell';
import { iconForAssociation } from './highlightIcons';
import { UpdateFieldModal } from './UpdateFieldModal';
import { useEditableHighlightField } from './useEditableHighlightField';
import { useOpenHighlightTarget } from './useOpenHighlightTarget';

type AssociationDefinition = Extract<
  HighlightDefinition,
  { kind: 'association' }
>;

interface Props {
  definition: AssociationDefinition;
  object: CrmObject;
  orgId: number;
}

export function AssociationHighlightCard({ definition, object, orgId }: Props) {
  const assoc = useMemo(
    () =>
      object.associations?.find(
        a => a.association_type_name === definition.associationType
      ),
    [object.associations, definition.associationType]
  );

  const targetId = assoc?.target_entity_id;
  const handleOpen = useOpenHighlightTarget(targetId);

  const [isEditing, setIsEditing] = useState(false);
  const editableField = useEditableHighlightField(
    orgId,
    object.entity_type_name,
    'association',
    definition.associationType
  );
  const handleEdit = useCallback(() => setIsEditing(true), []);
  const handleCloseEdit = useCallback(() => setIsEditing(false), []);

  // Resolve the target's display name (a stage label or any entity's name)
  // through the name-scoped resolver: it reads the store and, if the target
  // isn't cached, fetches ONLY the display-name properties rather than
  // hydrating the full object. getDisplayName resolves stage labels too
  // (ahoy_display_name → ahoy_name), so this covers stages and plain entities
  // alike. Undefined target is handled cleanly (no fetch, no name).
  const {
    displayName: resolvedName,
    isLoading: nameLoading,
    hasFailed: nameFailed,
  } = useEntityDisplayName(targetId, orgId);

  const result = useMemo(() => {
    if (!assoc || !targetId) {
      return {
        value: null,
        isLoading: false as const,
        isError: false as const,
      };
    }

    // Prefer the resolver's store-backed name; fall back to the query-scoped
    // associated_objects overlay for instant first paint before the resolver
    // settles. The overlay was the ONLY source before — it omits some targets
    // (e.g. ahoy_user owners), which is what surfaced "No owner" while the
    // association itself was present.
    const overlayObj = object.associated_objects?.[targetId];
    const overlayName = overlayObj ? getDisplayName(overlayObj, '') : '';
    const value = resolvedName ?? (overlayName !== '' ? overlayName : null);

    if (value) {
      return {
        value,
        isLoading: false as const,
        isError: false as const,
      };
    }

    if (nameLoading) {
      return {
        value: null,
        isLoading: true as const,
        isError: false as const,
      };
    }

    // Settled with nothing resolved — error only on an actual fetch failure;
    // otherwise fall through to the card's empty state.
    return {
      value: null,
      isLoading: false as const,
      isError: nameFailed,
    };
  }, [
    assoc,
    targetId,
    resolvedName,
    nameLoading,
    nameFailed,
    object.associated_objects,
  ]);

  return (
    <>
      <HighlightCardShell
        label={definition.label}
        result={result}
        icon={iconForAssociation(definition.associationType)}
        emptyText={`No ${definition.label.toLowerCase()}`}
        // Clicking the card opens the associated entity in the slideout
        // stack. Null when there's no association to open — the card stays a
        // static empty-state display.
        onClick={handleOpen}
        actionLabel={
          targetId
            ? `Open ${result.value ?? definition.label.toLowerCase()}`
            : undefined
        }
        onEdit={editableField ? handleEdit : undefined}
        editLabel={`Edit ${definition.label.toLowerCase()}`}
      />
      {isEditing && editableField && (
        <UpdateFieldModal
          isOpen
          onClose={handleCloseEdit}
          orgId={orgId}
          object={object}
          field={editableField}
        />
      )}
    </>
  );
}
